import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import {
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Trash2,
  FileSpreadsheet,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

const LOON_YEARS = [2026, 2027];
const PERIODS = Array.from({ length: 12 }, (_, i) => i + 1);

const fmtH = (v) => Number(v || 0).toLocaleString('nl-NL', { maximumFractionDigits: 2 });

export default function UrenImport() {
  const { toast } = useToast();

  const [locations, setLocations] = useState([]);
  const [slug, setSlug] = useState('');
  const [loonYear, setLoonYear] = useState(2026);
  const [period, setPeriod] = useState(5);
  const [file, setFile] = useState(null);

  const [report, setReport] = useState(null);
  const [overrides, setOverrides] = useState({}); // {raw_name: employee_id}
  const [locEmployees, setLocEmployees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [confirmSkips, setConfirmSkips] = useState(false);
  const [batches, setBatches] = useState([]);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await api.get('/uren-import/batches');
      setBatches(data.batches || []);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/locations');
        const sorted = (data || []).slice().sort((a, b) => a.name.localeCompare(b.name, 'nl'));
        setLocations(sorted);
        if (sorted.length) setSlug(sorted[0].slug);
      } catch {
        toast({ title: 'Kon locaties niet laden', variant: 'destructive' });
      }
    })();
    loadBatches();
  }, [toast, loadBatches]);

  // Load this location's employees for the unmatched-name override pickers.
  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const { data } = await api.get('/employees', { params: { location_slug: slug } });
        setLocEmployees(Array.isArray(data) ? data : []);
      } catch {
        setLocEmployees([]);
      }
    })();
  }, [slug]);

  const resetReport = () => {
    setReport(null);
    setOverrides({});
    setCommitted(null);
    setConfirmSkips(false);
  };

  const buildForm = (extraOverrides, allowSkips = false) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('location_id', slug);
    fd.append('loon_year', String(loonYear));
    fd.append('period', String(period));
    const ov = extraOverrides ?? overrides;
    if (ov && Object.keys(ov).length) fd.append('name_overrides', JSON.stringify(ov));
    if (allowSkips) fd.append('allow_skips', 'true');
    return fd;
  };

  const runDryRun = useCallback(async (ov) => {
    if (!file) {
      toast({ title: 'Kies eerst een CSV-bestand', variant: 'destructive' });
      return;
    }
    setBusy(true);
    setCommitted(null);
    try {
      const { data } = await api.post('/uren-import/dry-run', buildForm(ov), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(data);
    } catch (e) {
      toast({
        title: 'Dry-run mislukt',
        description: e.response?.data?.detail || 'Onbekende fout',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, slug, loonYear, period, overrides, toast]);

  const setOverride = (rawName, employeeId) => {
    const next = { ...overrides };
    if (employeeId) next[rawName] = employeeId;
    else delete next[rawName];
    setOverrides(next);
    runDryRun(next);
  };

  const runCommit = useCallback(async () => {
    if (!file) return;
    const allowSkips = !report?.ready_to_commit;
    setCommitting(true);
    try {
      const { data } = await api.post('/uren-import/commit', buildForm(undefined, allowSkips), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCommitted(data);
      toast({ title: 'Import voltooid', description: `Batch ${data.batch_id}` });
      loadBatches();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({
        title: 'Import mislukt',
        description: (typeof d === 'object' ? d?.message : d) || 'Onbekende fout',
        variant: 'destructive',
      });
    } finally {
      setCommitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, slug, loonYear, period, overrides, report, toast, loadBatches]);

  const deleteBatch = useCallback(async (batchId) => {
    if (!window.confirm(`Batch "${batchId}" verwijderen uit beide collecties?`)) return;
    try {
      await api.delete(`/uren-import/batch/${batchId}`);
      toast({ title: 'Batch verwijderd' });
      loadBatches();
    } catch (e) {
      toast({ title: 'Verwijderen mislukt', description: e.response?.data?.detail, variant: 'destructive' });
    }
  }, [toast, loadBatches]);

  const matchSummary = useMemo(() => {
    const m = report?.name_matches || [];
    return {
      matched: m.filter((x) => x.status === 'matched').length,
      unmatched: m.filter((x) => x.status === 'unmatched').length,
      ambiguous: m.filter((x) => x.status === 'ambiguous').length,
    };
  }, [report]);

  const skipCounts = useMemo(() => {
    const c = report?.counts || {};
    const leave = c.leave_rows || {};
    const importRows = (c.worked_rows || 0)
      + Object.values(leave).reduce((a, b) => a + (b || 0), 0);
    return { skippedRows: c.skipped_unmatched || 0, importRows };
  }, [report]);

  return (
    <div className="space-y-5" data-testid="page-uren-import">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Uren-import (oud systeem)</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Laad de <em>Gewerkte uren</em>-export van het oude systeem in voor een dry-run reconciliatie.
          De kolom <strong>Totaal</strong> is de leidende, uitbetaalde netto — die wordt gebruikt voor
          de loonberekening; SmartRoster's eigen berekende netto staat ernaast ter controle.
        </p>
      </div>

      {/* Upload form */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500">Locatie</label>
            <select
              data-testid="uren-import-location-select"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); resetReport(); }}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none"
            >
              {locations.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500">Loonjaar</label>
            <select
              data-testid="uren-import-year-select"
              value={loonYear}
              onChange={(e) => { setLoonYear(Number(e.target.value)); resetReport(); }}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none"
            >
              {LOON_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500">Periode</label>
            <select
              data-testid="uren-import-period-select"
              value={period}
              onChange={(e) => { setPeriod(Number(e.target.value)); resetReport(); }}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none"
            >
              {PERIODS.map((p) => <option key={p} value={p}>Periode {p}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500">CSV-bestand</label>
            <input
              type="file"
              accept=".csv,text/csv"
              data-testid="uren-import-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); resetReport(); }}
              className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium"
            />
          </div>
          <Button
            type="button"
            data-testid="uren-import-dryrun-button"
            onClick={() => runDryRun()}
            disabled={busy || !file || !slug}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Analyseer (dry-run)
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Verwacht formaat: kolommen <code>Datum</code> · <code>Medewerker</code> · <code>Locatie</code> ·
          <code> Omschrijving</code> · <code>Van</code> · <code>Tot</code> · <code>Totaal</code> ·
          <code> Opmerkingen</code> (scheidingsteken <code>;</code>). Datum als <code>JJJJ-MM-DD</code>,
          tijden als <code>UU:MM</code>, Totaal met decimale komma (bijv. <code>9,75</code>).
        </p>
      </div>

      {/* Dry-run report */}
      {report && (
        <div className="space-y-5" data-testid="uren-import-report">
          {/* Counts */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Gewerkte rijen" value={report.counts.worked_rows} testid="count-worked" />
            <StatCard
              label="Verlof (vak/ziek/bijz.)"
              value={`${report.counts.leave_rows.vakantie}/${report.counts.leave_rows.ziekte}/${report.counts.leave_rows.bijzonder_verlof}`}
              testid="count-leave"
            />
            <StatCard label="Overgeslagen" value={report.counts.skipped} testid="count-skipped" />
            <StatCard
              label="Namen gekoppeld"
              value={`${matchSummary.matched}✓ / ${matchSummary.unmatched + matchSummary.ambiguous}✗`}
              testid="count-names"
            />
          </div>

          {report.counts?.revalued_full_day_leave > 0 && (
            <div
              className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800"
              data-testid="uren-import-revalued-note"
            >
              <strong>{report.counts.revalued_full_day_leave}</strong> volledige verlofdag(en) herwaardeerd
              naar contract ÷ 5 (vaste medewerkers; oude flat-uren overschreven).
            </div>
          )}

          {report.counts?.skipped_oncall_leave > 0 && (
            <div
              className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800"
              data-testid="uren-import-oncall-leave-note"
            >
              <strong>{report.counts.skipped_oncall_leave}</strong> verlof/ziekte-regel(s) van
              oproepkrachten overgeslagen — oproep krijgt geen automatische ZK/VAK (alleen handmatig door een
              manager).
            </div>
          )}

          {report.counts?.km_shifts > 0 && (
            <div
              className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800"
              data-testid="uren-import-km-note"
            >
              Reiskosten: <strong>{report.counts.km_shifts}</strong> dienst(en) met km gespiegeld — totaal{' '}
              <strong>{report.km_total}</strong> km (export 700021).
            </div>
          )}

          {/* Name matches */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Naam-koppeling</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Naam in bestand</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Medewerker (SR)</TableHead>
                  <TableHead>Personeelsnr.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.name_matches.map((m) => (
                  <TableRow key={m.file_name} data-testid={`name-row-${m.status}`}>
                    <TableCell className="font-medium">{m.file_name}</TableCell>
                    <TableCell>
                      {m.status === 'matched' && (
                        <Badge className="border-emerald-300 bg-emerald-50 text-emerald-700" variant="outline">
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Gekoppeld{m.via_override ? ' (handmatig)' : ''}
                        </Badge>
                      )}
                      {m.status === 'unmatched' && (
                        <Badge className="border-rose-300 bg-rose-50 text-rose-700" variant="outline">
                          <XCircle className="mr-1 h-3 w-3" /> Niet gevonden
                        </Badge>
                      )}
                      {m.status === 'ambiguous' && (
                        <Badge className="border-amber-300 bg-amber-50 text-amber-700" variant="outline">
                          <AlertTriangle className="mr-1 h-3 w-3" /> Meerdere
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {m.status === 'matched' ? (
                        m.employee_name
                      ) : (
                        <select
                          data-testid={`override-select-${m.file_name}`}
                          value={overrides[m.file_name] || ''}
                          onChange={(e) => setOverride(m.file_name, e.target.value)}
                          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm focus:border-brand-amber focus:outline-none"
                        >
                          <option value="">— kies medewerker —</option>
                          {locEmployees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.name}{emp.personeelsnummer ? ` (${emp.personeelsnummer})` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">{m.personeelsnummer || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Hours reconciliation */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">
              Uren-reconciliatie — bestand (leidend) vs SR-berekend
            </h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Medewerker</TableHead>
                  <TableHead className="text-right">Bestand netto</TableHead>
                  <TableHead className="text-right">SR berekend</TableHead>
                  <TableHead className="text-right">Verschil</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.hours.per_employee.map((p) => (
                  <TableRow key={p.personeelsnummer || p.employee} data-testid="hours-row">
                    <TableCell className="font-medium">{p.employee}</TableCell>
                    <TableCell className="text-right">{fmtH(p.file_net)}</TableCell>
                    <TableCell className="text-right text-slate-500">{fmtH(p.sr_computed_net)}</TableCell>
                    <TableCell className="text-right">
                      <span className={Math.abs(p.delta) > 0.001 ? 'font-semibold text-amber-600' : 'text-slate-400'}>
                        {p.delta > 0 ? '+' : ''}{fmtH(p.delta)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50 font-semibold">
                  <TableCell>Totaal</TableCell>
                  <TableCell className="text-right">{fmtH(report.hours.file_net_total)}</TableCell>
                  <TableCell className="text-right">{fmtH(report.hours.sr_computed_net_total)}</TableCell>
                  <TableCell className="text-right text-amber-600">
                    {report.hours.delta > 0 ? '+' : ''}{fmtH(report.hours.delta)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Warnings */}
          {report.warnings.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="uren-import-warnings">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Waarschuwingen ({report.warnings.length})
              </div>
              <ul className="list-inside list-disc space-y-1 text-sm text-amber-700">
                {report.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Commit */}
          <div className="space-y-3">
            {!report.ready_to_commit && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                data-testid="uren-import-skip-confirm"
              >
                <label className="flex items-start gap-2 text-sm text-amber-800">
                  <input
                    type="checkbox"
                    data-testid="uren-import-skip-checkbox"
                    checked={confirmSkips}
                    onChange={(e) => setConfirmSkips(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-amber-300"
                  />
                  <span>
                    <strong>{skipCounts.skippedRows}</strong> regel(s) worden overgeslagen
                    (niet-gekoppelde medewerkers); <strong>{skipCounts.importRows}</strong> regel(s)
                    worden geïmporteerd. Doorgaan?
                  </span>
                </label>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                data-testid="uren-import-commit-button"
                onClick={runCommit}
                disabled={committing || (!report.ready_to_commit && !confirmSkips)}
              >
                {committing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
                {report.ready_to_commit ? 'Bevestig import' : 'Importeer gekoppelde regels'}
              </Button>
            </div>
          </div>

          {committed && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="uren-import-commit-success">
              <div className="flex items-center gap-2 font-semibold">
                <CheckCircle2 className="h-4 w-4" /> Import voltooid — batch {committed.batch_id}
              </div>
              <p className="mt-1">
                {committed.time_records_inserted} gewerkte records + {committed.leave_records_inserted} verlofrecords
                geschreven ({committed.skipped} overgeslagen).
              </p>
              {committed.skipped_count > 0 && (
                <p className="mt-1 text-emerald-700" data-testid="uren-import-skipped-names">
                  {committed.skipped_count} niet-gekoppelde medewerker(s) overgeslagen:{' '}
                  {committed.skipped_names.join(', ')}.
                </p>
              )}
              <Link
                to={`/loonperiode?location=${slug}`}
                className="mt-2 inline-flex items-center gap-1 font-medium text-emerald-700 underline"
                data-testid="uren-import-loonperiode-link"
              >
                Naar Loonperiode <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Existing batches */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="uren-import-batches">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Geïmporteerde batches</h3>
        {batches.length === 0 ? (
          <p className="text-sm text-slate-400">Nog geen imports.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch</TableHead>
                <TableHead>Locatie</TableHead>
                <TableHead>Periode</TableHead>
                <TableHead className="text-right">Records</TableHead>
                <TableHead className="text-right">Actie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.batch_id} data-testid={`batch-row-${b.batch_id}`}>
                  <TableCell className="font-mono text-xs">{b.batch_id}</TableCell>
                  <TableCell>{b.location_id || '—'}</TableCell>
                  <TableCell>{b.loon_year ? `${b.loon_year} · P${b.period}` : '—'}</TableCell>
                  <TableCell className="text-right">{b.time_records} uren / {b.leave_records} verlof</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={`batch-delete-${b.batch_id}`}
                      onClick={() => deleteBatch(b.batch_id)}
                      className="text-rose-600 hover:text-rose-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={testid}>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-slate-900">{value}</div>
    </div>
  );
}
